import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Icon,
  Input,
  InputGroup,
  InputRightElement,
  Avatar,
  useColorModeValue,
  Heading,
  IconButton,
  Spinner,
  Badge,
  Alert,
  AlertIcon,
  Flex,
} from '@chakra-ui/react';
import { Send, MessageCircle, Clock, AlertCircle } from 'lucide-react';
import { sendMessageToAnthropic } from '../services/anthropic';

// Methodist color palette
const methodistColors = {
  blue: '#0066CC',
  navy: '#032B44',
};

// Maximum number of questions allowed
const MAX_QUESTIONS = 10;

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: string;
}

const MobileCultureAssistant: React.FC = () => {
  const [inputValue, setInputValue] = useState('');
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: 'Welcome to the SentientIQ Cultural Quotient (CQ) Platform! I\'m JIMI, your guide to enhancing cultural awareness and building inclusive healthcare practices. How can I assist you today?',
      sender: 'assistant',
      timestamp: '9:00 AM',
    }
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [questionCount, setQuestionCount] = useState(0);

  // Create a ref for the messages container
  const messagesEndRef = useRef<HTMLDivElement>(null);
  
  // Scroll to bottom whenever messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Handle send message
  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading) return;
    
    // Check if question limit reached
    if (questionCount >= MAX_QUESTIONS) {
      return;
    }
    
    // Add user message
    const newUserMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      sender: 'user',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    
    setMessages(prev => [...prev, newUserMessage]);
    setInputValue('');
    setIsLoading(true);
    setQuestionCount(prev => prev + 1);
    
    try {
      // Convert messages to format expected by Anthropic API
      const anthropicMessages = messages.concat(newUserMessage).map(msg => ({
        role: msg.sender as 'user' | 'assistant',
        content: msg.text
      }));
      
      // Get response from Anthropic API
      const response = await sendMessageToAnthropic(anthropicMessages);
      
      // Add AI response
      const newAiMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: response,
        sender: 'assistant',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages(prev => [...prev, newAiMessage]);
    } catch (error) {
      console.error('Error getting response:', error);
      
      // Add error message
      const errorMessage: Message = {
        id: (Date.now() + 1).toString(),
        text: 'I apologize, but I encountered an error processing your request. Please try again.',
        sender: 'assistant',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      };
      
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const textColor = useColorModeValue('gray.700', 'gray.300');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  return (
    <Box height="100%" display="flex" flexDirection="column" pt="10%">
      {/* Header - more minimalist */}
      <Box p={4} borderBottom="1px" borderColor={borderColor} bg={cardBg}>
        <HStack spacing={3} justify="space-between">
          <HStack spacing={3}>
            <Avatar
              size="sm"
              bg={`${methodistColors.blue}20`}
              icon={<Icon as={MessageCircle} color={methodistColors.blue} boxSize={4} />}
            />
            <VStack align="start" spacing={0}>
              <Text fontSize="sm" fontWeight="medium" color={textColor}>JIMI Chat</Text>
              <Text fontSize="xs" color={secondaryTextColor}>
                <Icon as={Clock} boxSize={3} mr={1} />
                Online
              </Text>
            </VStack>
          </HStack>
          <Badge colorScheme={questionCount >= MAX_QUESTIONS ? "red" : "blue"}>
            {questionCount}/{MAX_QUESTIONS} Questions
          </Badge>
        </HStack>
      </Box>

      {/* Chat messages - with scrolling container */}
      <Box flex={1} overflowY="auto" p={4}>
        <VStack spacing={4} align="stretch">
          {messages.map((message) => (
            <Box
              key={message.id}
              bg={message.sender === 'user' ? 'gray.50' : `${methodistColors.blue}10`}
              color={textColor}
              p={4}
              borderRadius="lg"
              borderWidth="1px"
              borderColor={message.sender === 'user' ? 'gray.100' : `${methodistColors.blue}30`}
              maxW="90%"
              alignSelf={message.sender === 'user' ? 'flex-end' : 'flex-start'}
              fontSize="sm"
            >
              <Text>{message.text}</Text>
              <Text fontSize="xs" color={secondaryTextColor} textAlign="right" mt={1}>
                {message.timestamp}
              </Text>
            </Box>
          ))}
          {isLoading && (
            <Flex justify="center" py={4}>
              <Spinner color={methodistColors.blue} size="sm" />
            </Flex>
          )}
          {questionCount >= MAX_QUESTIONS && (
            <Alert status="info" borderRadius="md" size="sm">
              <AlertIcon as={AlertCircle} boxSize={4} />
              <Text fontSize="xs">You've reached the maximum number of questions for this session.</Text>
            </Alert>
          )}
          <div ref={messagesEndRef} /> {/* Empty div for scrolling */}
        </VStack>
      </Box>

      {/* Input area */}
      <Box p={4} borderTop="1px" borderColor={borderColor} bg={cardBg}>
        <InputGroup size="md">
          <Input
            placeholder={questionCount >= MAX_QUESTIONS ? "Question limit reached" : "Type your message..."}
            bg={inputBg}
            borderRadius="full"
            fontSize="sm"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              // Prevent space bar from propagating to parent elements
              if (e.key === ' ') {
                e.stopPropagation();
              }
              if (e.key === 'Enter') {
                handleSendMessage();
              }
            }}
            _focus={{
              borderColor: methodistColors.blue,
              boxShadow: `0 0 0 1px ${methodistColors.blue}`,
            }}
            isDisabled={isLoading || questionCount >= MAX_QUESTIONS}
          />
          <InputRightElement>
            {isLoading ? (
              <Spinner size="sm" color={methodistColors.blue} mr={2} />
            ) : (
              <IconButton
                aria-label="Send message"
                icon={<Icon as={Send} boxSize={4} />}
                size="sm"
                colorScheme="blue"
                variant="ghost"
                onClick={handleSendMessage}
                isDisabled={!inputValue.trim() || questionCount >= MAX_QUESTIONS}
              />
            )}
          </InputRightElement>
        </InputGroup>
      </Box>
    </Box>
  );
};

export default MobileCultureAssistant;
